import React from 'react';
import { Link } from 'react-router-dom';
import { statsData } from '../data/mockData';
import { Button } from './ui/button';
import { ArrowRight, Network, Cable, Layers, RefreshCw } from 'lucide-react';
import VendorConnectivityScene from './hero/VendorConnectivityScene';

const Hero = () => {
  return (
    <section className="relative min-h-screen overflow-hidden bg-dark-ellipse">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-4 items-center min-h-[78vh]">
          {/* Left: Copy */}
          <div className="text-left" data-testid="hero-content">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
              <Network className="w-4 h-4 text-[#8bcaf9]" />
              <span className="text-white text-sm font-medium">Conectividad API en tiempo real</span>
            </div>

            <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Todos tus vendors.{' '}
              <span className="text-[#8bcaf9]">Un solo marketplace.</span>
            </h1>

            <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl leading-relaxed">
              asisant conecta tu negocio con más de 100 fabricantes mediante integraciones API directas:
              aprovisionamiento automático de licencias, sincronización de catálogos y precios,
              y facturación unificada en una sola plataforma.
            </p>

            {/* Feature pills */}
            <div className="flex flex-wrap gap-3 mb-10">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <Cable className="w-4 h-4 text-[#8bcaf9]" />
                <span className="text-white text-sm">Integración directa con vendors</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <RefreshCw className="w-4 h-4 text-[#8bcaf9]" />
                <span className="text-white text-sm">Aprovisionamiento automático</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
                <Layers className="w-4 h-4 text-[#8bcaf9]" />
                <span className="text-white text-sm">Catálogo multivendor unificado</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link to="/cloud-marketplace">
                <Button
                  data-testid="hero-cta-marketplace"
                  size="lg"
                  className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-8 py-6 rounded-full text-lg transition-all hover:scale-105 hover:shadow-xl group"
                >
                  Explorar el Marketplace
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/contacto?source=demo">
                <Button
                  data-testid="hero-cta-demo"
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg transition-all hover:scale-105 bg-transparent"
                >
                  Solicitar una demo
                </Button>
              </Link>
            </div>

            {/* Trust badge */}
            <div className="mt-12 flex items-center gap-3 flex-wrap">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/30" />
                ))}
              </div>
              <span className="text-white/70 text-sm">+ de 100 soluciones con integración directa</span>
            </div>
          </div>

          {/* Right: 3D Vendor Connectivity Scene */}
          <div className="relative flex justify-center items-center h-[360px] sm:h-[440px] lg:h-[580px] overflow-visible">
            <div className="scale-[0.45] sm:scale-[0.62] lg:scale-100 origin-center shrink-0">
              <VendorConnectivityScene />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {statsData.map((stat, idx) => (
            <div
              key={idx}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all hover:scale-105 hover:border-white/30"
            >
              <p className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</p>
              <p className="text-white/70 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
